import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Alert, Badge, Card, Spinner } from "../../../components/ui";
import { AppShell } from "../../../layouts/AppShell";
import {
  getVoiceConversationConfig,
  getVoiceOpenAIKeyStatus,
  getVoicePersona,
  getVoiceRagStatus,
  type VoiceConversationConfig,
} from "../api/voiceApi";
import { OpenAIKeyCard } from "../components/OpenAIKeyCard";
import { PersonaCard } from "../components/PersonaCard";
import { RagCard } from "../components/RagCard";

function readinessTitle(config: VoiceConversationConfig): string {
  if (!config.requirements.api_key_ready) {
    return "尚未就緒：請先完成 API Key 驗證";
  }
  if (!config.requirements.persona_ready) {
    return "尚未就緒：請先確認 Persona 設定";
  }
  if (!config.requirements.rag_ready) {
    return "部分就緒：可開始一般對話，知識庫問答尚未完成";
  }
  return "已就緒：可開始語音對話";
}

function readinessVariant(config: VoiceConversationConfig): string {
  if (config.ready && config.requirements.rag_ready && config.requirements.persona_ready) {
    return "success";
  }
  if (config.requirements.api_key_ready) {
    return "warning";
  }
  return "secondary";
}

export function VoiceAssistantPage() {
  const configQuery = useQuery({
    queryKey: ["voice", "conversation-config"],
    queryFn: getVoiceConversationConfig,
  });
  const statusQuery = useQuery({
    queryKey: ["voice", "openai-key-status"],
    queryFn: getVoiceOpenAIKeyStatus,
  });
  const ragStatusQuery = useQuery({
    queryKey: ["voice", "rag-status"],
    queryFn: getVoiceRagStatus,
    refetchInterval: (query) => (query.state.data?.state === "building" ? 2000 : false),
  });
  const personaQuery = useQuery({
    queryKey: ["voice", "persona"],
    queryFn: getVoicePersona,
  });
  const config = configQuery.data;

  return (
    <AppShell title="AI 語音助教設定" breadcrumb={["AI 語音助教", "設定"]}>
      <Alert variant="info">
        這個頁面用於完成 AI 語音助教的前置設定。正式對話請前往
        {" "}
        <Link to="/voice/conversation">AI 語音對話頁</Link>
        。
      </Alert>
      {configQuery.isLoading && <Spinner label="載入對話 readiness" />}
      {configQuery.isError && <Alert variant="danger">無法載入對話 readiness，請稍後再試。</Alert>}
      {!configQuery.isLoading && !configQuery.isError && config && (
        <Card title="對話 Readiness 總覽">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="text-muted">目前狀態</span>
            <Badge variant={readinessVariant(config)}>{readinessTitle(config)}</Badge>
          </div>
          <p className="text-muted mb-3">
            這個狀態用來判斷是否已具備開始語音對話的條件；若知識庫尚未就緒，仍可先進行一般對話。
          </p>
          <ul className="mb-0 pl-3">
            <li>API Key 驗證：{config.requirements.api_key_ready ? "已完成" : "未完成"}</li>
            <li>RAG 知識庫：{config.requirements.rag_ready ? "已完成" : "未完成"}</li>
            <li>Persona 設定：{config.requirements.persona_ready ? "已完成" : "未完成"}</li>
          </ul>
        </Card>
      )}
      {statusQuery.isLoading && <Spinner label="載入 API Key 狀態" />}
      {statusQuery.isError && <Alert variant="danger">無法載入 API Key 狀態，請稍後再試。</Alert>}
      {!statusQuery.isLoading && !statusQuery.isError && <OpenAIKeyCard status={statusQuery.data} />}
      {ragStatusQuery.isLoading && <Spinner label="載入 RAG 狀態" />}
      {ragStatusQuery.isError && <Alert variant="danger">無法載入 RAG 狀態，請稍後再試。</Alert>}
      {!ragStatusQuery.isLoading && !ragStatusQuery.isError && (
        <RagCard status={ragStatusQuery.data} keyStatus={statusQuery.data} />
      )}
      {personaQuery.isLoading && <Spinner label="載入 Persona 設定" />}
      {personaQuery.isError && <Alert variant="danger">無法載入 Persona 設定，請稍後再試。</Alert>}
      {!personaQuery.isLoading && !personaQuery.isError && <PersonaCard persona={personaQuery.data} />}
    </AppShell>
  );
}
